import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as dayjs from 'dayjs';
import { FormatDatePipe } from 'src/app/modules/shared/pipes/format-date.pipe';
import { HolidayBookingService } from '../../../holiday-booking-page/services/holiday-booking.service';
import { IHolidayDetail } from '../../interfaces/iholiday-detail';
import { IHolidayDetailSolution } from '../../interfaces/iholiday-detail-solution';
import { Subscription } from 'rxjs';


@Component({
  selector: 'detail-flight',
  templateUrl: './detail-flight.component.html',
  styleUrls: ['./detail-flight.component.scss']
})
export class DetailFlightComponent implements OnInit, OnDestroy {

  allSolutions: IHolidayDetailSolution[];
  datesInRange: any;
  solutionsToShow: IHolidayDetailSolution[];
  datesWithSolutions: any[];
  isHolidayCustom: boolean = false;

  holidayDetailsSubscription: Subscription;

  selectedSolution: IHolidayDetailSolution;
  selectedSolutionSubscription: Subscription;

  selectedDate: Date;
  dateFrom
  dateTo

  index: number;
  config = {
    breakpoints: {
      320: {
        centeredSlides: false,
        slidesPerView: 2.25,
        spaceBetween: 8,
      },
      480: {
        centeredSlides: false,
        slidesPerView: 3.5,
        spaceBetween: 16,
      },
      768: {
        centeredSlides: false,
        slidesPerView: 4.5,
        spaceBetween: 16,
      }
    },

    navigation: false,
    pagination: false
 }

  public loadingStatus = false;
  public loadingMessage = 'Stiamo ricercando le soluzioni...';

  constructor(
    private _formatDatePipe: FormatDatePipe,
    private _holidayBookingService: HolidayBookingService,
    private _route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this._route.paramMap.subscribe(e => {
      this.selectedDate = dayjs(e.get('dateFrom'), 'DD-MM-YYYY').toDate();
      this.dateFrom = e.get('dateFrom');
      this.dateTo = e.get('dateTo');
      //  Selected Solution Subscription
      this.selectedSolutionSubscription = this._holidayBookingService.selectedSolution$.subscribe(selectedSolution => {
        this.selectedSolution = selectedSolution;
      });
      //  HolidayDetails Subscription
      this.holidayDetailsSubscription = this._holidayBookingService.holidayDetails$.subscribe(holidayDetails => {
        this.allSolutions = holidayDetails?.solutions || [];
        this.isHolidayCustom = holidayDetails.isCustom;
        this.initializeDates();
      });
    });
  }

  ngOnDestroy(): void {
    this.holidayDetailsSubscription.unsubscribe();
    this.selectedSolutionSubscription.unsubscribe();
  }

  //  Si occupa di trovare le date che presentano soluzioni e di mostrare quelle disponibili per la data inizialmente selezionata
  initializeDates() {
    this.datesWithSolutions = this.getDatesWithSolutions();
    this.showSolutionsForSelectedDate();
    this.slideToSelectedSoluctionDate();
  }

  //  Porta lo slider delle date a quella corrispondente alla selectedSolution
  slideToSelectedSoluctionDate() {
    const selectedSolutionSlideIndex = this.datesWithSolutions?.findIndex(dateObj => this.areDatesEquals(dateObj.date, this.selectedDate));
    this.index = selectedSolutionSlideIndex || 0;
  }

  //  Restituisce un array di date che presentano almeno una soluzione
  getDatesWithSolutions() {
    const allDates = this.allSolutions.map(sol => sol.dateFrom);
    const allUniqueDates = [];
    allDates.forEach( date => {
      if(!allUniqueDates.some( d => this.areDatesEquals(date, d))) {
        allUniqueDates.push(date)
      }
    })
    allUniqueDates.sort( (a,b) => a - b )
    return allUniqueDates;
  }

  // Controlla se due date sono uguali o meno
  areDatesEquals(date1, date2) {
    return this._formatDatePipe.transform(date1) === this._formatDatePipe.transform(date2);
  }

  //  Ciò che avviene quando l'utente clicca su una soluzione alternativa
  onClickOnAlternativeSolution(newSolution) {
    this._holidayBookingService.setData('selectedSolution', newSolution)
  }

  // Ciò che avviene quando l'utente clicca su una data alternativa
  onClickOnAlternativeDate(date) {
    if (this.selectedDate === date) return;
    this.selectedDate = date;
    this.showSolutionsForSelectedDate();
  }

  //  Restituisce lista di soluzioni disponibili per la data selezionata
  showSolutionsForSelectedDate() {
    this.loadingStatus = true;
    this.solutionsToShow = this.allSolutions.filter(sol => this.areDatesEquals(this.selectedDate, sol.dateFrom));
    this.solutionsToShow = this.removeDuplicateDepartureAirport(this.solutionsToShow);
    // this.solutionsToShow.forEach( sol => sol.isLoadingLiveInfo = true ) // serve per mostrare il loader
    this.getSolutionsLiveInfo();
  }

  // Rimuove eventuali soluzioni con aeroporto di partenza uguale
  //TODO questa è una soluzione temporanea per aggirare il problema del doppio aeroporto restituito talvolta per errore dalla chiamata di dettaglio
  removeDuplicateDepartureAirport(solutions) {
    const filteredSolutions = [];
    solutions.forEach(sol => {
      if (!filteredSolutions.some(filtered => filtered.airport === sol.airport)) {
        filteredSolutions.push(sol);
      }
    });
    return filteredSolutions;
  }

  //  passato un indice (riferito ad una posizione nell'array delle solution da mostrare) ottiene le additionalInfo per tutte le soluzioni presenti nell'array da quell'indice in giù
  //  NB: funzione ricorsiva
  getSolutionsLiveInfo() {
    const promises = this.solutionsToShow.map((sol, index) => {
      // Controlla se le informazioni aggiuntive sono già state aggiunte in precedenza
      if (sol.journey) {
        return Promise.resolve();
      }
      return this._holidayBookingService.getSolutionAdditionalLiveInfo(sol)
        .catch((error) => {
          console.error('Errore durante la richiesta delle informazioni aggiuntive:', error);
          this.solutionsToShow.splice(index, 1);
        });
    });

    Promise.all(promises)
      .then(() => {
        this.loadingStatus = false;
        this.findCheapestSolutions();
      })
      .catch((error) => {
        console.error('Errore durante il caricamento delle informazioni aggiuntive:', error);
        this.loadingStatus = false;
      });
  }

  //  Individua la/le solution più economica/he fra quelle visibili all'utente
  findCheapestSolutions() {
    let cheapestPrice = Math.trunc(this.solutionsToShow[0]?.totalPrice);
    this.solutionsToShow.forEach(solution => {
      if (Math.trunc(solution?.totalPrice) <= cheapestPrice) {
        cheapestPrice = Math.trunc(solution.totalPrice);
      }
    });
    //  imposto proprietà isCheapest a true per tutte le solution con il prezzo più basso (potrebbero essere più di una)
    this.solutionsToShow.forEach(solution => {
      solution.isCheapest = (solution.totalPrice && Math.trunc(solution.totalPrice) === cheapestPrice) ? true : false;
    });
  }

}
